import { useState, type ReactElement } from 'react';
import type { Attachment } from '@chatapp-inbox/shared';
import { attachmentContentUrl } from '../../api';
import { KIND_LABEL, formatBytes } from './attachments';
import { ImageViewer } from './ImageViewer';
import type { LocalSend } from './outbox';
import styles from './AttachmentView.module.css';

export interface AttachmentViewProps {
  attachment: Attachment;
  /** Envio desta aba ainda não confirmado: o arquivo vem da memória, com o progresso do envio. */
  local: LocalSend | null;
  /** Quem mandou, para os nomes acessíveis ("Foto enviada por Maria"). */
  author: string;
}

/** "PDF", "Word"…: o que a pessoa reconhece de um documento, pelo tipo ou pela extensão do nome. */
export function documentTypeLabel(attachment: Pick<Attachment, 'mimeType' | 'fileName'>): string {
  const mime = attachment.mimeType.toLowerCase();
  if (mime === 'application/pdf') return 'PDF';
  if (mime.includes('wordprocessingml') || mime === 'application/msword') return 'Word';
  if (mime.includes('spreadsheetml') || mime === 'application/vnd.ms-excel') return 'Excel';
  if (mime.includes('presentationml') || mime === 'application/vnd.ms-powerpoint') {
    return 'PowerPoint';
  }
  if (mime === 'text/plain') return 'Texto';
  const ext = attachment.fileName?.includes('.') ? attachment.fileName.split('.').pop() : '';
  return ext && ext.length <= 5 ? ext.toUpperCase() : 'Arquivo';
}

const ICON = {
  width: 28,
  height: 28,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
} as const;

function DocumentIcon(): ReactElement {
  return (
    <svg {...ICON} className={styles.icon}>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4M9 12h6M9 16h6" />
    </svg>
  );
}

/** O estado do ENVIO local, em palavras (o arquivo ainda está subindo, ou a mensagem ainda sai). */
function localProgress(local: LocalSend | null): { text: string; value: number | null } | null {
  const file = local?.attachment;
  if (!local || !file) return null;
  if (local.state === 'queued') return { text: 'Na fila…', value: null };
  if (local.state !== 'sending') return null;
  if (file.progress < 1) {
    return { text: `Enviando arquivo… ${Math.floor(file.progress * 100)}%`, value: file.progress };
  }
  return { text: 'Enviando mensagem…', value: null };
}

/**
 * O arquivo de uma mensagem: imagem (abre ampliada), vídeo e áudio com controles, documento como
 * cartão para abrir/baixar. Enquanto o arquivo recebido não baixou (ou falhou), um aviso no lugar.
 * Tudo vem da mesma origem, por rota autenticada: nenhum endereço público.
 */
export function AttachmentView({ attachment, local, author }: AttachmentViewProps): ReactElement {
  const [viewing, setViewing] = useState(false);
  const [broken, setBroken] = useState(false);
  const isLocal = local?.attachment !== undefined;
  const progress = localProgress(local);
  const name = attachment.fileName ?? KIND_LABEL[attachment.kind];
  const url = isLocal
    ? (local.attachment?.previewUrl ?? null)
    : attachmentContentUrl(attachment.id);
  const downloadUrl = attachmentContentUrl(attachment.id, { download: true });
  const size = attachment.sizeBytes === null ? '' : formatBytes(attachment.sizeBytes);
  const label = `${KIND_LABEL[attachment.kind]} enviada por ${author}`;

  const progressBar = progress && (
    <div className={styles.progress}>
      {progress.value !== null && (
        <progress
          max={100}
          value={Math.floor(progress.value * 100)}
          aria-label={`Envio de ${name}`}
          className={styles.bar}
        />
      )}
      <span role="status">{progress.text}</span>
    </div>
  );

  if (!isLocal && attachment.status === 'pending') {
    return (
      <div className={styles.notice} role="status">
        <span aria-hidden="true" className={styles.spinner} />
        <span>Baixando arquivo…</span>
      </div>
    );
  }
  if (!isLocal && attachment.status === 'failed') {
    return (
      <p className={`${styles.notice} ${styles.failed}`}>
        Não foi possível baixar {attachment.fileName ? `"${attachment.fileName}"` : 'este arquivo'}.
        Peça ao cliente para enviá-lo de novo.
      </p>
    );
  }

  const showAsImage =
    (attachment.kind === 'image' || attachment.kind === 'sticker') && url !== null && !broken;
  if (showAsImage) {
    const image = (
      <img
        className={attachment.kind === 'sticker' ? styles.sticker : styles.image}
        src={url}
        alt={label}
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
    return (
      <div className={styles.media}>
        {isLocal ? (
          image
        ) : (
          <button
            type="button"
            className={styles.imageButton}
            aria-label={`Ampliar: ${label}`}
            onClick={() => setViewing(true)}
          >
            {image}
          </button>
        )}
        {progressBar}
        {viewing && (
          <ImageViewer
            src={url}
            downloadHref={downloadUrl}
            label={name}
            onClose={() => setViewing(false)}
          />
        )}
      </div>
    );
  }

  if (!isLocal && attachment.kind === 'video') {
    return (
      <div className={styles.media}>
        {/* Vídeo de cliente: não há legenda a oferecer. */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          className={styles.video}
          controls
          preload="metadata"
          src={url ?? undefined}
          aria-label={label}
        />
        <a className={styles.link} href={downloadUrl}>
          Baixar vídeo{size ? ` (${size})` : ''}
        </a>
      </div>
    );
  }

  if (!isLocal && attachment.kind === 'audio') {
    return (
      <div className={styles.media}>
        {/* Áudio de cliente: não há transcrição a oferecer. */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio
          className={styles.audio}
          controls
          preload="metadata"
          src={url ?? undefined}
          aria-label={attachment.voice ? `Nota de voz enviada por ${author}` : label}
        />
      </div>
    );
  }

  // Documento — e o que não dá para mostrar de outro jeito (vídeo/áudio ainda subindo, imagem quebrada).
  const canOpen = !isLocal && attachment.mimeType === 'application/pdf';
  return (
    <div className={styles.doc}>
      <DocumentIcon />
      <div className={styles.docInfo}>
        <span className={styles.docName} dir="auto">
          {broken ? `${name} (não foi possível carregar)` : name}
        </span>
        <span className={styles.docMeta}>
          {attachment.kind === 'document'
            ? documentTypeLabel(attachment)
            : KIND_LABEL[attachment.kind]}
          {size ? ` · ${size}` : ''}
        </span>
      </div>
      {!isLocal && (
        <div className={styles.docActions}>
          {canOpen && (
            <a
              className={styles.link}
              href={attachmentContentUrl(attachment.id)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Abrir ${name}`}
            >
              Abrir
            </a>
          )}
          <a className={styles.link} href={downloadUrl} aria-label={`Baixar ${name}`}>
            Baixar
          </a>
        </div>
      )}
      {progressBar}
    </div>
  );
}
